import { useState } from 'react';
import { Lightbulb, AlertTriangle, TrendingUp, Info, ChevronRight, ChevronDown } from 'lucide-react';
import type { MetricInsight } from './types';

export function AIInsightBox({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-blue-500/5 border border-blue-500/20 rounded-lg p-4 ${className}`}>
      <div className="flex gap-3">
        <Lightbulb className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-muted-foreground leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

// Default metric explanations (code-based, always available)
const METRIC_EXPLANATIONS: Record<string, { whatItMeasures: string; target: string; impact: string }> = {
  lcp: {
    whatItMeasures: 'Largest Contentful Paint measures how long it takes for the main content of your page to load. This is typically the hero image, main heading, or largest text block that visitors see first.',
    target: 'Good: < 2.5s | Needs Improvement: 2.5-4s | Poor: > 4s',
    impact: 'A slow LCP means visitors stare at a blank or partially loaded page. 53% of mobile users abandon sites that take over 3 seconds to load.',
  },
  cls: {
    whatItMeasures: 'Cumulative Layout Shift measures visual stability — how much elements move around as the page loads. It captures when buttons, images, or text unexpectedly shift position.',
    target: 'Good: < 0.1 | Needs Improvement: 0.1-0.25 | Poor: > 0.25',
    impact: 'High CLS frustrates users who accidentally click wrong buttons or lose their reading place. This is especially critical for reservation and order buttons.',
  },
  fcp: {
    whatItMeasures: 'First Contentful Paint measures when the browser first renders any content — text, images, or canvas elements. It\'s the first visual feedback that the page is loading.',
    target: 'Good: < 1.8s | Needs Improvement: 1.8-3s | Poor: > 3s',
    impact: 'FCP affects perceived performance. Users who see content quickly are more likely to wait for the rest of the page, reducing bounce rates.',
  },
  ttfb: {
    whatItMeasures: 'Time to First Byte measures server responsiveness — how long until the browser receives the first byte of data from your server after requesting a page.',
    target: 'Good: < 0.8s | Needs Improvement: 0.8-1.8s | Poor: > 1.8s',
    impact: 'Slow TTFB indicates server or hosting issues. Everything else waits for the server to respond first, so this is the foundation of page speed.',
  },
};

export function MetricExplainer({
  insight,
  metricKey
}: {
  insight: MetricInsight | null;
  metricKey?: 'lcp' | 'cls' | 'fcp' | 'ttfb';
}) {
  const [expanded, setExpanded] = useState(false);

  // Get default explanation for this metric
  const defaultExplanation = metricKey ? METRIC_EXPLANATIONS[metricKey] : null;

  // If no insight AND no default explanation, don't render
  if (!insight && !defaultExplanation) return null;

  return (
    <div className="mt-3 space-y-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-xs text-blue-400/80 hover:text-blue-400 transition-colors"
      >
        <Info className="h-3 w-3" />
        {expanded ? 'Hide explanation' : 'What does this mean?'}
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      </button>

      {expanded && (
        <div className="bg-secondary/50 rounded-lg p-4 space-y-3 text-sm">
          {/* What it measures - prefer insight, fallback to default */}
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">What it measures</p>
            <p className="text-foreground/90 leading-relaxed">
              {insight?.whatItMeans || defaultExplanation?.whatItMeasures}
            </p>
          </div>

          {/* Target thresholds - always show from defaults */}
          {defaultExplanation && (
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Target thresholds</p>
              <p className="text-foreground/70 font-mono text-xs">{defaultExplanation.target}</p>
            </div>
          )}

          {/* Your score means - insight only */}
          {insight?.yourScore && (
            <div className="border-t border-border/50 pt-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                <span className="text-blue-400/70">Insight:</span> Your score means
              </p>
              <p className="text-foreground/90 leading-relaxed">{insight.yourScore}</p>
            </div>
          )}

          {/* How to improve - insight or default impact */}
          <div className="border-t border-border/50 pt-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
              {insight?.howToImprove ? (
                <span><span className="text-blue-400/70">Insight:</span> How to improve</span>
              ) : (
                'Why it matters'
              )}
            </p>
            <p className="text-foreground/90 leading-relaxed">
              {insight?.howToImprove || defaultExplanation?.impact}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export function SoWhatCard({
  title,
  insight,
  type = 'info',
}: {
  title: string;
  insight: string | null;
  type?: 'info' | 'warning' | 'success';
}) {
  if (!insight) return null;

  const styles = {
    info: 'bg-blue-500/5 border-blue-500/20',
    warning: 'bg-orange-500/5 border-orange-500/20',
    success: 'bg-green-500/5 border-green-500/20',
  };

  const icons = {
    info: <Lightbulb className="h-4 w-4 text-blue-400" />,
    warning: <AlertTriangle className="h-4 w-4 text-orange-400" />,
    success: <TrendingUp className="h-4 w-4 text-green-400" />,
  };

  return (
    <div className={`border rounded-lg p-4 ${styles[type]}`}>
      <div className="flex items-start gap-3">
        {icons[type]}
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{title}</p>
          <p className="text-sm text-foreground leading-relaxed">{insight}</p>
        </div>
      </div>
    </div>
  );
}
